import { Dispatch, SetStateAction, useState } from "react"

import { DEVICE_INDEX_DELIMITER } from "../../../../../components/commissioning/gt368/gt368Form"
import { parseErrorAsObj } from "../../../../../errors/errorProcessors"
import { IAppError } from "../../../../../errors/errorTypes"
import { snackbar } from "../../../../../utils/general/generalUtils"
import { postClientProtected } from "../../../clients/clients"
import {
    CxFormClasses,
    GT_368_FORM_CLASS,
} from "../../../types/commissioning/installer/launchCxFormTypes"
import { SUBMIT_CX_FORM } from "../../../urls/hybrid"

const CX_DEVICE_SYS_REF_KEY = "deviceSysRef"

interface UseSubmitCxRes {
    error: IAppError | undefined
    isSubmitting: boolean
    postResponse: () => Promise<void>
}

/**
 * Hook to handle the submission of commissioning form by an installer
 * @returns {UseSubmitCxRes}
 */
const useSubmitCx = (
    mode: CxFormClasses,
    formVersion: number,
    applicationId: string,
    setIsSuccessDialogOpen: Dispatch<SetStateAction<boolean>>,
    formData: Record<string, unknown>
): UseSubmitCxRes => {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)

    const postResponse = async (): Promise<void> => {
        try {
            setIsSubmitting(true)

            let body
            if (mode === GT_368_FORM_CLASS) {
                const { siteData, files, ...devicesMap } = formData

                /*
                In form data, the output of the device form for each device is
                stored as an object with the key being the device name and the
                value being the form data for that device. This is converted to
                an array of objects with each object containing the device Id
                and the form data for that device.
                */
                const devices = Object.entries(devicesMap).map(
                    ([device, deviceData]) => ({
                        ...(deviceData ?? {}),
                        [CX_DEVICE_SYS_REF_KEY]: device.split(
                            DEVICE_INDEX_DELIMITER
                        )[0],
                    })
                )
                body = {
                    applicationId,
                    cxFormClass: mode,
                    cxFormVersion: formVersion,
                    formData: {
                        siteData,
                        devices,
                        files,
                    },
                }
            } else {
                body = {
                    applicationId,
                    cxFormClass: mode,
                    cxFormVersion: formVersion,
                    formData,
                }
            }
            await postClientProtected(SUBMIT_CX_FORM, body)
            snackbar("Commissioning Details Submitted Successfully", "success")
            setIsSuccessDialogOpen(true)
            setError(undefined)
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            setError(errorObj)
        } finally {
            setIsSubmitting(false)
        }
    }

    return {
        error,
        isSubmitting,
        postResponse,
    }
}

export default useSubmitCx
